import { wrapOrphanImagesInParagraphs } from '../../../components/editor/slate-html';

/**
 * Ensure every <table>, <tr>, <td> has quill-table attributes (table_id, row_id, cell_id)
 * so standard HTML tables display correctly in the signature editor and compose body.
 * Call this before normalizeSignatureTableIds when preparing signature HTML.
 */
export function ensureSignatureTableIds(html: string): string {
  if (!html || typeof document === 'undefined') return html;
  if (!html.includes('<table')) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  let t = 0;
  let r = 0;
  let c = 0;
  const tables = div.querySelectorAll('table');
  tables.forEach((table) => {
    const tableId = `t${t++}`;
    if (!table.getAttribute('table_id')) table.setAttribute('table_id', tableId);
    const rows = table.querySelectorAll(':scope > tbody > tr, :scope > tr');
    rows.forEach((tr) => {
      const rowId = `r${r++}`;
      if (!tr.getAttribute('row_id')) tr.setAttribute('row_id', rowId);
      const cells = tr.querySelectorAll(':scope > td, :scope > th');
      cells.forEach((cell) => {
        const cellId = `c${c++}`;
        if (!cell.getAttribute('cell_id')) cell.setAttribute('cell_id', cellId);
        if (!cell.getAttribute('table_id')) cell.setAttribute('table_id', tableId);
        if (!cell.getAttribute('row_id')) cell.setAttribute('row_id', rowId);
      });
    });
  });
  return div.innerHTML;
}

/**
 * Normalize quill-table HTML that has table_id/row_id/cell_id="null" so Quill can parse and display it.
 * Use when loading signature HTML into the editor or when inserting into the compose body.
 */
export function normalizeSignatureTableIds(html: string): string {
  if (!html || !html.includes('table_id="null"')) return html;
  let tableCounter = 0;
  let rowCounter = 0;
  let cellCounter = 0;
  return html
    .replace(/table_id="null"/gi, () => `table_id="t${tableCounter++}"`)
    .replace(/row_id="null"/gi, () => `row_id="r${rowCounter++}"`)
    .replace(/cell_id="null"/gi, () => `cell_id="c${cellCounter++}"`);
}

function escapeHtmlPlain(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const PRESERVE_SPACES_SKIP_TAGS = new Set(['PRE', 'STYLE', 'SCRIPT']);

function replaceConsecutiveAsciiSpaces(text: string): string {
  return text.replace(/ {2,}/g, (run) => ` ${'\u00a0'.repeat(run.length - 1)}`);
}

/**
 * Email HTML collapses runs of ASCII spaces. Convert 2+ spaces to nbsp so signature
 * layout (e.g. gaps between name and phone on one line) survives preview and send.
 */
export function preserveSignatureSpaces(html: string): string {
  if (!html) return html;

  if (typeof document === 'undefined') {
    return html.replace(/>([^<]*)</g, (full, text: string) => {
      if (!/ {2,}/.test(text)) return full;
      return `>${replaceConsecutiveAsciiSpaces(text)}<`;
    });
  }

  const root = document.createElement('div');
  root.innerHTML = html;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode: Node | null;
  while ((textNode = walker.nextNode())) {
    const parent = textNode.parentElement;
    if (!parent) continue;
    let el: Element | null = parent;
    let skip = false;
    while (el && el !== root) {
      if (PRESERVE_SPACES_SKIP_TAGS.has(el.tagName)) {
        skip = true;
        break;
      }
      el = el.parentElement;
    }
    if (skip) continue;
    const text = textNode.textContent ?? '';
    if (!/ {2,}/.test(text)) continue;
    textNode.textContent = replaceConsecutiveAsciiSpaces(text);
  }
  return root.innerHTML;
}

const SIGNATURE_LINE_STYLE = 'margin:0;padding:0;line-height:1.35;font-size:14px;font-weight:normal';

const HTML_TAG_RE =
  /<(?:p|div|br|h[1-6]|table|span|strong|b|em|i|a|img|font|td|tr|tbody|thead|ul|ol|li)\b/i;

/** Remove RFC 3676 `--` delimiter line from plain or HTML signatures. */
export function stripSignatureDelimiter(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return trimmed;

  if (!HTML_TAG_RE.test(trimmed)) {
    return trimmed.replace(/^--[ \t]*\r?\n?/, '').trimStart();
  }

  if (typeof document === 'undefined') {
    return trimmed
      .replace(/^<h[1-6][^>]*>\s*--\s*<\/h[1-6]>\s*/i, '')
      .replace(/^<p[^>]*>\s*--\s*<\/p>\s*/i, '')
      .replace(/^<div[^>]*>\s*--\s*<\/div>\s*/i, '');
  }

  const root = document.createElement('div');
  root.innerHTML = trimmed;
  while (root.firstElementChild) {
    const text = (root.firstElementChild.textContent ?? '').trim();
    if (text === '--' || text === '-- ') {
      root.firstElementChild.remove();
      continue;
    }
    break;
  }
  return root.innerHTML.trim();
}

/** Slate signatures saved with Heading 1 per line — normalize to normal text blocks. */
export function normalizeSignatureHeadings(html: string): string {
  if (!html || !/<h[1-6]\b/i.test(html)) return html;

  if (typeof document === 'undefined') {
    return html.replace(/<\/?h[1-6]\b[^>]*>/gi, (tag) =>
      tag.startsWith('</') ? '</div>' : `<div style="${SIGNATURE_LINE_STYLE}">`,
    );
  }

  const root = document.createElement('div');
  root.innerHTML = html;
  root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
    const line = document.createElement('div');
    line.setAttribute('style', SIGNATURE_LINE_STYLE);
    const innerHtml = heading.innerHTML.trim();
    if (innerHtml) {
      line.innerHTML = innerHtml;
    } else {
      line.textContent = '\u00a0';
    }
    heading.replaceWith(line);
  });
  return root.innerHTML;
}

/** Wrap plain-text signatures (no HTML tags) so line breaks and spacing render in compose. */
export function ensureSignatureHtmlStructure(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return trimmed;
  if (HTML_TAG_RE.test(trimmed)) {
    return trimmed;
  }
  return trimmed
    .split('\n')
    .map((line) => {
      const content = escapeHtmlPlain(line) || '&nbsp;';
      return `<div style="${SIGNATURE_LINE_STYLE}">${content}</div>`;
    })
    .join('');
}

/**
 * Prepare signature HTML for the signature editor: preserve headings, bold, links, etc.
 * Does not run normalizeSignatureHeadings (that flattening is for compose display only).
 */
export function prepareSignatureHtmlForEditor(html: string): string {
  if (!html) return html;
  let out = stripSignatureDelimiter(html);
  out = ensureSignatureHtmlStructure(out);
  out = wrapOrphanImagesInParagraphs(out);
  return normalizeSignatureTableIds(ensureSignatureTableIds(out));
}

/**
 * Prepare signature HTML for compose: strip delimiter, fix heading blocks, ensure structure.
 */
export function prepareSignatureHtml(html: string): string {
  if (!html) return html;
  let out = prepareSignatureHtmlForEditor(html);
  out = normalizeSignatureHeadings(out);
  out = preserveSignatureSpaces(out);
  return out;
}

